import { Injectable, inject } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { catchError, from, map, of } from 'rxjs';

/** Service for checking browser permissions. */
@Injectable({
  providedIn: 'root',
})
export class BrowserPermissionsService {
  private logger = inject(NGXLogger);

  /** Checks if the user has blocked local network access, which is required for some features. */
  checkLocalNetworkAccessBlocked() {
    try {
      return from(
        // biome-ignore lint/suspicious/noExplicitAny: Types for Permissions API are not well-supported across browsers
        navigator.permissions.query({ name: 'local-network-access' as any })
      ).pipe(
        map((status) => {
          this.logger.debug(
            'Local-network-access: permission state is',
            status.state
          );
          switch (status.state) {
            case 'denied':
              this.logger.error(
                'Local-network-access:User has blocked local network access. Direct them to site settings.'
              );
              break;
            case 'prompt':
              this.logger.debug(
                'Local-network-access: The browser will show a popup on the next request.'
              );
              break;
            case 'granted':
              this.logger.debug(
                'Local-network-access: Local network access is granted.'
              );
              break;
            default:
              break;
          }

          // We consider it blocked if the state is "denied". If it's "prompt",
          // it means the browser will ask the user on the next request, so we
          // don't consider it blocked yet.
          return status.state === 'denied';
        }),
        catchError((err) => {
          this.logger.warn(
            'Error checking local network access permission:',
            err
          );
          return of(false); // Assume not blocked if we can't check the permission state
        })
      );
    } catch (err) {
      this.logger.warn(
        'Permissions API not supported or error checking local network access:',
        err
      );
      return of(false); // Assume not blocked if we can't check the permission state
    }
  }
}
